import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AronAccessLevel, AronReasoningMode } from "../types";
import type { FloatRect } from "../utils/floatGeometry";

/** Default + bounds for the detached-dock width (px). Mirrored in AronDockHost. */
export const ARON_DOCK_DEFAULT_WIDTH = 440;

interface AronUiState {
  // Sticky UI preferences (per the convention: Zustand only for auth + UI state).
  modelMode: AronReasoningMode;
  accessLevel: AronAccessLevel;
  autopilotDefault: boolean;
  setAutopilotDefault: (v: boolean) => void;
  /** Domyślne „Wyłącz limit kroków" dla NOWEJ rozmowy (tylko uprawnieni do YOLO). */
  unlimitedStepsDefault: boolean;
  setUnlimitedStepsDefault: (v: boolean) => void;
  /** Zaznaczenie „Kompresuj obrazy" przy polu pisania. Wysyłka i tak niesie tę
   *  decyzję per wiadomość — to jest tylko zapamiętane ustawienie pola wyboru,
   *  żeby ktoś, kto pracuje na zdjęciach z terenu, nie klikał go przy każdym pytaniu. */
  compressImagesDefault: boolean;
  setCompressImagesDefault: (v: boolean) => void;
  setModelMode: (m: AronReasoningMode) => void;
  setAccessLevel: (a: AronAccessLevel) => void;

  // Detached side-dock — a conversation "wydzielona" into a resizable right-hand
  // panel that lives next to whatever page is open (mounted globally in
  // ProtectedLayout). State is persisted so the dock survives reloads/navigation.
  dockOpen: boolean;
  dockConvId: string | null;
  dockWidth: number;
  /** Open the dock; pass a conversation id to dock that conversation (omit to keep current). */
  openDock: (convId?: string | null) => void;
  closeDock: () => void;
  setDockConvId: (id: string | null) => void;
  setDockWidth: (w: number) => void;

  // Wydzielona rozmowa ma dwie postacie: przypięty panel z prawej ("side", jak
  // dotąd) albo pływające okno nad stroną ("float"). Domyślnie zostaje przy
  // starym zachowaniu — pływak włącza się jednym kliknięciem i tak samo wraca.
  dockMode: "side" | "float";
  /** Pozycja i rozmiar pływaka; null = policz przy pierwszym otwarciu. */
  floatRect: FloatRect | null;
  /** Zwinięty do paska (tytuł + sygnalizacja), treść schowana. */
  floatMinimized: boolean;
  /** Rozmiar sprzed przyciągnięcia/rozciągnięcia — bez tego okno na pełnym
   * ekranie nie ma jak wrócić do swojej wielkości. */
  floatRestoreRect: FloatRect | null;
  setDockMode: (m: "side" | "float") => void;
  /** `snapped` = rozmiar narzucony przyciągnięciem; zapamiętuje poprzedni. */
  setFloatRect: (r: FloatRect, opts?: { snapped?: boolean }) => void;
  setFloatMinimized: (v: boolean) => void;
  /** Rozciągnij na cały ekran / wróć do poprzedniego rozmiaru. */
  toggleFloatMaximized: (full: FloatRect) => void;
  /** Zdejmij stan „przyciągnięty", zachowując podany rozmiar (start przeciągania). */
  restoreFloatRect: (r: FloatRect) => void;

  // Pływak trzyma WŁASNE karty — tak jak pełna strona. Panel boczny zostaje
  // jednowątkowy (jest przyklejony do strony), pływak jest od pracy na kilku
  // rozmowach naraz.
  floatTabs: { key: string; convId: string | null }[];
  floatActiveKey: string | null;
  /** Otwórz rozmowę jako kartę pływaka (null = nowa rozmowa). Otwarta już
   * rozmowa jest tylko uaktywniana. */
  openFloatTab: (convId?: string | null) => void;
  closeFloatTab: (key: string) => void;
  activateFloatTab: (key: string) => void;
  /** Karta robocza dostała rozmowę utworzoną na serwerze. */
  adoptFloatTab: (key: string, convId: string) => void;
}

let floatSeq = 0;
const floatTabKey = () =>
  `f${++floatSeq}-${Math.random().toString(36).slice(2, 8)}`;

/** Karty pływaka po wejściu w tryb pływający — bierzemy to, co stało w doku. */
function seedTabs(convId: string | null) {
  return [{ key: floatTabKey(), convId }];
}

/** Tryb pracy nowej rozmowy, dopóki nikt nie wybierze innego. Jedno miejsce, bo
 *  picker ustawia go też na pierwszym miejscu listy. */
export const ARON_DEFAULT_MODE: AronReasoningMode = "standard";

export const useAronUiStore = create<AronUiState>()(
  persist(
    (set) => ({
      modelMode: ARON_DEFAULT_MODE,
      accessLevel: "read_only",
      autopilotDefault: false,
      setAutopilotDefault: (autopilotDefault) => set({ autopilotDefault }),
      unlimitedStepsDefault: false,
      setUnlimitedStepsDefault: (unlimitedStepsDefault) =>
        set({ unlimitedStepsDefault }),
      compressImagesDefault: false,
      setCompressImagesDefault: (compressImagesDefault) => set({ compressImagesDefault }),
      setModelMode: (modelMode) => set({ modelMode }),
      setAccessLevel: (accessLevel) => set({ accessLevel }),

      dockOpen: false,
      dockConvId: null,
      dockWidth: ARON_DOCK_DEFAULT_WIDTH,
      openDock: (convId) =>
        set((s) => {
          const id = convId !== undefined ? convId : s.dockConvId;
          const base = { dockOpen: true, dockConvId: id };
          // W trybie pływającym samo ustawienie identyfikatora nie wystarczy: okno
          // pokazuje KARTY, więc bez dodania karty tej rozmowy użytkownik widział
          // pustą albo cudzą (zgłoszenie właściciela 2026-08-23). Karta istniejąca
          // zostaje uaktywniona, nowa dopisuje się na końcu i staje się bieżącą.
          if (s.dockMode !== "float" || !id) return base;
          const istniejaca = s.floatTabs.find((t) => t.convId === id);
          if (istniejaca) return { ...base, floatActiveKey: istniejaca.key };
          const key = `float-${id}`;
          return {
            ...base,
            floatTabs: [...s.floatTabs, { key, convId: id }],
            floatActiveKey: key,
          };
        }),
      closeDock: () => set({ dockOpen: false }),
      setDockConvId: (dockConvId) => set({ dockConvId }),
      setDockWidth: (dockWidth) => set({ dockWidth }),

      dockMode: "side",
      floatRect: null,
      floatMinimized: false,
      floatRestoreRect: null,
      floatTabs: [],
      floatActiveKey: null,

      setDockMode: (dockMode) =>
        set((s) => {
          if (dockMode === "float") {
            // Wejście w pływak: jeśli nie ma jeszcze kart, zaczynamy od tej
            // rozmowy, która stała w panelu bocznym.
            if (s.floatTabs.length) return { dockMode };
            const tabs = seedTabs(s.dockConvId);
            return { dockMode, floatTabs: tabs, floatActiveKey: tabs[0].key };
          }
          // Powrót do panelu: bok pokazuje rozmowę z aktywnej karty pływaka.
          const active = s.floatTabs.find((t) => t.key === s.floatActiveKey);
          return {
            dockMode,
            dockConvId: active ? active.convId : s.dockConvId,
            floatMinimized: false,
          };
        }),
      setFloatRect: (floatRect, opts) =>
        set((s) => ({
          floatRect,
          // Ręczna zmiana rozmiaru kasuje pamięć — od tej chwili to JEST rozmiar
          // okna, nie chwilowe przyciągnięcie.
          floatRestoreRect: opts?.snapped
            ? (s.floatRestoreRect ?? s.floatRect)
            : null,
        })),
      setFloatMinimized: (floatMinimized) => set({ floatMinimized }),

      toggleFloatMaximized: (full) =>
        set((s) => {
          if (s.floatRestoreRect) {
            return { floatRect: s.floatRestoreRect, floatRestoreRect: null };
          }
          return { floatRect: full, floatRestoreRect: s.floatRect };
        }),

      restoreFloatRect: (floatRect) => set({ floatRect, floatRestoreRect: null }),

      openFloatTab: (convId = null) =>
        set((s) => {
          if (convId) {
            const open = s.floatTabs.find((t) => t.convId === convId);
            if (open) return { floatActiveKey: open.key, floatMinimized: false };
            // Pusta karta robocza przyjmuje rozmowę zamiast mnożyć „Nowa rozmowa".
            const draft = s.floatTabs.find(
              (t) => t.convId === null && t.key === s.floatActiveKey,
            );
            if (draft) {
              return {
                floatMinimized: false,
                floatTabs: s.floatTabs.map((t) =>
                  t.key === draft.key ? { ...t, convId } : t,
                ),
              };
            }
          }
          const tab = { key: floatTabKey(), convId };
          return {
            floatMinimized: false,
            floatTabs: [...s.floatTabs, tab],
            floatActiveKey: tab.key,
          };
        }),

      closeFloatTab: (key) =>
        set((s) => {
          const i = s.floatTabs.findIndex((t) => t.key === key);
          if (i === -1) return {};
          const floatTabs = s.floatTabs.filter((t) => t.key !== key);
          // Zamknięcie ostatniej karty zostawia pustą kartę roboczą — pływak bez
          // niczego byłby ramką, w którą nie da się nic napisać.
          if (!floatTabs.length) {
            const tabs = seedTabs(null);
            return { floatTabs: tabs, floatActiveKey: tabs[0].key };
          }
          const activeKey =
            s.floatActiveKey === key
              ? (floatTabs[i] ?? floatTabs[i - 1]).key
              : s.floatActiveKey;
          return { floatTabs, floatActiveKey: activeKey };
        }),

      activateFloatTab: (floatActiveKey) =>
        set({ floatActiveKey, floatMinimized: false }),

      adoptFloatTab: (key, convId) =>
        set((s) => ({
          floatTabs: s.floatTabs.map((t) => (t.key === key ? { ...t, convId } : t)),
        })),
    }),
    {
      name: "elg5-aron-ui",
      // Standardowy (Opus 5, adaptive thinking) is the default for new conversations:
      // the tier that holds up on numbers and judgement, which is what people actually
      // ask ARON for. The picker is otherwise a sticky preference, so bumping this
      // version re-forces the current default onto existing clients whose persisted
      // choice drifted elsewhere.
      // NOTE: this store is what actually decides the mode for a real user — it is sent
      // on create and overrides the backend serializer default. Changing the default
      // means changing BOTH, or the backend change is invisible in the product.
      version: 4,
      migrate: (persisted, version) => {
        const state = persisted as AronUiState;
        if (version < 4 && state) {
          return { ...state, modelMode: ARON_DEFAULT_MODE };
        }
        return state;
      },
    },
  ),
);
